<script setup lang="ts">
import { useLanguage } from '@/composables/useLanguage';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { availableLanguages, setLanguage, currentLanguage } = useLanguage();

// Filter out the current language from dropdown options
const dropdownLanguages = computed(() => {
    return availableLanguages.filter((lang) => lang.code !== currentLanguage.value.code);
});

// Reactive state for language dropdown
const isLanguageDropdownOpen = ref(false);
const isMobile = ref(false);

// Check if device is mobile
const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768;
};

// Toggle language dropdown (for mobile)
const toggleLanguageDropdown = () => {
    if (isMobile.value) {
        isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value;
    }
};

// Open language dropdown (for desktop hover)
const openLanguageDropdown = () => {
    if (!isMobile.value) {
        isLanguageDropdownOpen.value = true;
    }
};

// Close language dropdown
const closeLanguageDropdown = () => {
    isLanguageDropdownOpen.value = false;
};

// Click outside handler (for mobile)
const handleClickOutside = (event: Event) => {
    if (isMobile.value) {
        const target = event.target as HTMLElement;
        if (!target.closest('.fs-language-dropdown')) {
            closeLanguageDropdown();
        }
    }
};

// Handle language selection
const handleLanguageSelect = (langCode: string) => {
    setLanguage(langCode);
    closeLanguageDropdown();
};

// Lifecycle hooks
onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <header class="fs-header">
        <v-container>
            <div class="fs-header-content">
                <!-- TAROM Logo -->
                <div class="fs-header-logo">
                    <a :href="route('home')">
                        <img src="/images/header/tarom-logo.svg" :alt="t('common.tarom_logo')" />
                    </a>
                    <a href="#">
                        <img src="/images/header/skyteam-logo.svg" :alt="t('common.skyteam_logo')" />
                    </a>
                </div>

                <!-- Language Dropdown -->
                <div class="fs-language-dropdown" @mouseenter="openLanguageDropdown" @mouseleave="closeLanguageDropdown">
                    <button class="fs-language-btn" @click="toggleLanguageDropdown">
                        <img :src="currentLanguage.flag" :alt="currentLanguage.name" class="fs-language-flag" />
                        <span class="fs-language-text">{{ currentLanguage.name }}</span>
                        <i class="mdi mdi-chevron-down fs-language-chevron"></i>
                    </button>

                    <div class="fs-language-menu" :class="{ show: isLanguageDropdownOpen }">
                        <button v-for="lang in dropdownLanguages" :key="lang.code" class="fs-language-item" @click="handleLanguageSelect(lang.code)">
                            <img :src="lang.flag" :alt="lang.name" class="fs-language-flag" />
                            <span>{{ lang.name }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </v-container>
    </header>
</template>

<style scoped>
/* Language dropdown chevron rotation */
.fs-language-dropdown .fs-language-menu.show ~ .fs-language-btn .fs-language-chevron {
    transform: rotate(180deg);
}

/* Flag image styles */
.fs-language-flag {
    width: 20px;
    height: 15px;
    object-fit: cover;
    border-radius: 2px;
    margin-right: 8px;
}
.fs-language-btn > .fs-language-flag {
    margin-right: 0;
}
.fs-language-item {
    display: flex;
    align-items: center;
}

.fs-language-btn {
    display: flex;
    align-items: center;
}
</style>
